import { Injectable, signal } from '@angular/core';

export type ProductType = 'product' | 'service';

export interface Product {
  id: string;
  name: string;
  type: ProductType;
  description: string;
  unitPrice: number;
  taxRate: string;
  sku: string;
  externalSourceName: string;
  externalSourceId: string;
  active: boolean;
}

export interface InvoiceLineItem {
  productId: string | null;
  name: string;
  description: string;
  qty: number;
  unitPrice: number;
  taxRate: string;
}

export type InvoiceStatus = 'draft' | 'paid' | 'pending' | 'overdue';

export interface Invoice {
  id: string;
  client: string;
  site: string;
  amount: number;
  status: InvoiceStatus;
  due: string;
  issued: string;
  allowedMethods: string[];
  items: InvoiceLineItem[];
  customerNotes: string;
  internalNotes: string;
  sendViaEmail: boolean;
  recipientEmail: string;
}

@Injectable({ providedIn: 'root' })
export class PaymentsDataService {
  readonly taxRates = [
    { key: 'standard', label: 'Standard (1%)', rate: 1 },
    { key: 'reduced', label: 'Reduced (0.5%)', rate: 0.5 },
    { key: 'zero', label: 'Zero (0%)', rate: 0 },
    { key: 'exempt', label: 'Exempt', rate: 0 },
  ];

  readonly clients = ['TechBuild Corp', 'Summit Properties', 'Mountain West REIT', 'Apex Retail Group', 'Denver Metro Properties'];
  readonly sites = ['Denver HQ', 'Aurora DC', 'Boulder Research', 'Colorado Springs', 'Fort Collins Depot', 'Lakewood'];
  readonly methods = ['Credit Card', 'ACH', 'Check', 'Cash'];

  readonly products = signal<Product[]>([
    { id: 'PRD-001', name: 'HVAC Filter Replacement', type: 'product', description: 'Standard replacement filter for rooftop HVAC units.', unitPrice: 85, taxRate: 'standard', sku: 'HVAC-FLT-01', externalSourceName: '', externalSourceId: '', active: true },
    { id: 'PRD-002', name: 'Site Inspection (Standard)', type: 'service', description: 'Routine site inspection covering safety and compliance checklist.', unitPrice: 250, taxRate: 'standard', sku: 'SVC-INSP-01', externalSourceName: '', externalSourceId: '', active: true },
    { id: 'PRD-003', name: 'Emergency Callout', type: 'service', description: 'After-hours emergency response and repair.', unitPrice: 400, taxRate: 'standard', sku: 'SVC-EMRG-01', externalSourceName: '', externalSourceId: '', active: true },
    { id: 'PRD-004', name: 'Monitoring Subscription', type: 'service', description: 'Remote site monitoring and alerting service.', unitPrice: 120, taxRate: 'zero', sku: 'SUB-MON-01', externalSourceName: '', externalSourceId: '', active: true },
    { id: 'PRD-005', name: 'Legacy Sensor Kit', type: 'product', description: 'Discontinued sensor kit, kept for existing service contracts.', unitPrice: 60, taxRate: 'exempt', sku: 'HW-SENS-00', externalSourceName: '', externalSourceId: '', active: false },
  ]);

  readonly invoices = signal<Invoice[]>([
    { id: 'INV-2026-048', client: 'TechBuild Corp', site: 'Denver HQ', amount: 4850, status: 'paid', due: '2026-06-01', issued: '2026-05-15', allowedMethods: ['Credit Card', 'ACH'], items: [], customerNotes: '', internalNotes: '', sendViaEmail: false, recipientEmail: '' },
    { id: 'INV-2026-049', client: 'Summit Properties', site: 'Boulder Research', amount: 2200, status: 'pending', due: '2026-06-20', issued: '2026-06-01', allowedMethods: ['Credit Card'], items: [], customerNotes: '', internalNotes: '', sendViaEmail: false, recipientEmail: '' },
    { id: 'INV-2026-050', client: 'Mountain West REIT', site: 'Fort Collins Depot', amount: 7500, status: 'pending', due: '2026-06-25', issued: '2026-06-05', allowedMethods: ['ACH'], items: [], customerNotes: '', internalNotes: '', sendViaEmail: false, recipientEmail: '' },
    { id: 'INV-2026-047', client: 'Apex Retail Group', site: 'Lakewood', amount: 1800, status: 'overdue', due: '2026-05-28', issued: '2026-05-01', allowedMethods: ['Check'], items: [], customerNotes: '', internalNotes: '', sendViaEmail: false, recipientEmail: '' },
    { id: 'INV-2026-046', client: 'TechBuild Corp', site: 'Aurora DC', amount: 3600, status: 'paid', due: '2026-05-15', issued: '2026-04-28', allowedMethods: ['Credit Card', 'Cash'], items: [], customerNotes: '', internalNotes: '', sendViaEmail: false, recipientEmail: '' },
  ]);

  taxRateLabel(key: string): string {
    return this.taxRates.find(t => t.key === key)?.label ?? key;
  }

  taxRateValue(key: string): number {
    return this.taxRates.find(t => t.key === key)?.rate ?? 0;
  }

  getInvoice(id: string): Invoice | undefined {
    return this.invoices().find(i => i.id === id);
  }

  nextInvoiceId(): string {
    const year = new Date().getFullYear();
    const ids = this.invoices()
      .filter(i => i.id.startsWith(`INV-${year}-`))
      .map(i => parseInt(i.id.split('-')[2], 10));
    const lastId = Math.max(...ids, 50);
    return `INV-${year}-` + String(lastId + 1).padStart(3, '0');
  }

  addInvoice(invoice: Invoice) {
    this.invoices.update(is => [invoice, ...is]);
  }

  updateInvoice(id: string, changes: Partial<Invoice>) {
    this.invoices.update(is => is.map(i => i.id === id ? { ...i, ...changes } : i));
  }

  deleteInvoice(id: string) {
    this.invoices.update(is => is.filter(i => i.id !== id));
  }

  markPaid(id: string) {
    this.updateInvoice(id, { status: 'paid' });
  }

  addProduct(product: Product) {
    this.products.update(ps => [product, ...ps]);
  }

  updateProduct(id: string, changes: Partial<Product>) {
    this.products.update(ps => ps.map(p => p.id === id ? { ...p, ...changes } : p));
  }

  deleteProduct(id: string) {
    this.products.update(ps => ps.filter(p => p.id !== id));
  }

  nextProductId(): string {
    const ids = this.products().map(p => parseInt(p.id.split('-')[1], 10));
    const lastId = Math.max(...ids, 0);
    return 'PRD-' + String(lastId + 1).padStart(3, '0');
  }
}
